/**
 * True when the current URL is still carrying Supabase / OAuth return parameters.
 * In that case we must not <Navigate> away, or the PKCE `code` (or `error` details)
 * is lost before the client can read it.
 */
export function hasOauthReturnParams(search: string, hash: string): boolean {
  const searchParams = new URLSearchParams(search);
  if (searchParams.has("code")) return true;
  if (searchParams.has("error")) return true;
  if (!hash || hash.length <= 1) return false;
  const h = hash.slice(1);
  return h.includes("access_token") || h.includes("error");
}

/** Google / Supabase returned an OAuth error in the query string (not a PKCE code). */
export function getOAuthErrorFromQuery(search: string): { error: string; errorDescription: string } | null {
  const p = new URLSearchParams(search);
  if (!p.has("error")) {
    return null;
  }
  return {
    error: p.get("error") ?? "unknown",
    errorDescription: p.get("error_description")?.replaceAll("+", " ") ?? "",
  };
}

/** Magic-link verify redirects with `token_hash` + `type` when PKCE is enabled on the project. */
export function hasMagicLinkTokenHashInQuery(search: string): boolean {
  const params = new URLSearchParams(search);
  const tokenHash = params.get("token_hash")?.trim() ?? "";
  if (tokenHash === "") {
    return false;
  }
  const type = params.get("type")?.trim().toLowerCase() ?? "";
  return type === "magiclink" || type === "email" || type === "signup" || type === "recovery";
}

/** True if we are waiting for Supabase to exchange a PKCE `code` (not an OAuth error return). */
export function isAwaitingPkceExchange(search: string): boolean {
  if (getOAuthErrorFromQuery(search) !== null) {
    return false;
  }
  return new URLSearchParams(search).has("code");
}

/**
 * True while auth tokens are still in the URL and the Supabase client has not finished establishing
 * a session. Protected routes must not redirect away during this window or hash/query params are lost.
 */
export function isAwaitingSupabaseUrlSession(search: string, hash: string): boolean {
  if (isAwaitingPkceExchange(search)) {
    return true;
  }
  if (hasMagicLinkTokenHashInQuery(search)) {
    return true;
  }
  if (!hash || hash.length <= 1) {
    return false;
  }
  const fragment = hash.slice(1);
  return fragment.includes("access_token") || fragment.includes("refresh_token");
}

/** Tokens from admin magic-link verify when Supabase redirects with an implicit hash fragment. */
export function parseImplicitSessionTokensFromHash(hash: string): {
  access_token: string;
  refresh_token: string;
} | null {
  if (!hash || hash.length <= 1) {
    return null;
  }
  const params = new URLSearchParams(hash.slice(1));
  const accessToken = params.get("access_token")?.trim() ?? "";
  const refreshToken = params.get("refresh_token")?.trim() ?? "";
  if (accessToken === "" || refreshToken === "") {
    return null;
  }
  return { access_token: accessToken, refresh_token: refreshToken };
}

/** Remove OAuth / magic-link params from the address bar after the client consumes them. */
export function clearAuthParamsFromUrl(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.history.replaceState({}, "", window.location.pathname + window.location.search);
}
